/**
 * Plant Breeder's Rights (PBR) — IP Australia labelling constants.
 *
 * Source of truth: IP Australia "Industry guidelines for labelling"
 * (Industry-guidelines-for-labelling.pdf, IP Australia).
 *
 * These strings are LEGALLY REQUIRED wording — do not paraphrase, do not
 * abbreviate, do not translate. Render verbatim. Any change here must come
 * from IP Australia's published guidelines.
 *
 * See also: feedback_pbr_symbol_display.md in the project memory for the full
 * placement / sizing rules and the implementation contract.
 */

/**
 * Mandatory warning text that must accompany the PBR logo wherever it is
 * rendered for a granted-or-provisional protected variety.
 *
 * VERBATIM from IP Australia industry guidelines. Italicise the Act name
 * (the consuming component handles that via JSX).
 */
export const PBR_WARNING_TEXT =
  'Unauthorised commercial propagation or any sale, conditioning, export, import or stocking of propagating material of this variety is an infringement under the Plant Breeder’s Rights Act 1994.';

/**
 * The name of the Act, isolated so consumers can italicise it (the verbatim
 * presentation requirement) without splitting the warning string itself.
 */
export const PBR_ACT_NAME = 'Plant Breeder’s Rights Act 1994';

/**
 * Pre-application "test marketing" notice — Section 43(6) of the PBR Act.
 * Used for plants sold during the window before a PBR application is lodged.
 *
 * Substitute the expiry date (computed via {@link computeTestMarketingExpiry})
 * for the {date} placeholder when rendering.
 */
export const PBR_TEST_MARKETING_TEMPLATE =
  'Eligibility of this plant as a registrable plant variety under Section 43(6) of the Plant Breeder’s Rights Act 1994 will expire on {date}.';

/** Minimum logo width on physical tie-on / push-in plant labels. */
export const PBR_MIN_LOGO_WIDTH_MM = 14;

/** Minimum warning text point size on physical labels. */
export const PBR_MIN_WARNING_PT = 5;

/**
 * Approved logo colours per IP Australia industry guidelines. PMS 562 is
 * IP Australia's teal/green spot colour; closest sRGB approximation below.
 * Use {@link PBR_COLORS.pms562} for screen rendering when colour is required;
 * print artwork should reference the actual PMS 562 ink.
 */
export const PBR_COLORS = {
  black: '#000000',
  pms562: '#00866B', // sRGB approximation of PMS 562
} as const;

export type PbrColor = keyof typeof PBR_COLORS;

/**
 * Compute the Section 43(6) test-marketing expiry date.
 *
 * Per IP Australia: 12 months from first Australian sale; 4 years from first
 * overseas sale; 6 years for trees and specified vines.
 *
 * @param firstSaleDate    ISO date string (YYYY-MM-DD) of the first sale.
 * @param category         'au' | 'overseas' | 'tree_or_vine'.
 */
export function computeTestMarketingExpiry(
  firstSaleDate: string,
  category: 'au' | 'overseas' | 'tree_or_vine',
): string {
  const d = new Date(`${firstSaleDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid firstSaleDate: ${firstSaleDate}`);
  }
  if (category === 'au') {
    d.setUTCFullYear(d.getUTCFullYear() + 1);
  } else if (category === 'overseas') {
    d.setUTCFullYear(d.getUTCFullYear() + 4);
  } else {
    d.setUTCFullYear(d.getUTCFullYear() + 6);
  }
  return d.toISOString().slice(0, 10);
}

/**
 * PBR protection status values for which the symbol + warning MUST be shown.
 * Matches Cultivar.protection_status from types/cultivar.ts.
 */
export const PBR_PROTECTED_STATUSES = ['pending', 'protected'] as const;
export type PbrProtectedStatus = (typeof PBR_PROTECTED_STATUSES)[number];

export function isPbrProtected(
  status: string | null | undefined,
): status is PbrProtectedStatus {
  return !!status && (PBR_PROTECTED_STATUSES as readonly string[]).includes(status);
}
